
import React from 'react';
import EntityDetailLayout from '../EntityDetailLayout';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { ExternalLink, Copy, CreditCard, Calendar, DollarSign } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { UpgradeStatusBadge } from '@/components/dashboard/subscription/UpgradeStatusBadge';

interface SubscriptionDetailsViewProps {
  id: string;
  data?: any; // Subscription data passed directly from the table
  onClose: () => void;
}

const SubscriptionDetailsView: React.FC<SubscriptionDetailsViewProps> = ({ id, data, onClose }) => {
  const { toast } = useToast();
  
  // Use the data passed directly instead of fetching
  const subscription = data;

  const handleCopyId = () => {
    navigator.clipboard.writeText(id);
    toast({
      title: "ID copied!",
      description: "Subscription ID has been copied to clipboard."
    });
  };

  const handleViewInStripe = () => {
    window.open(`https://dashboard.stripe.com/subscriptions/${id}`, '_blank');
  };

  const renderFooter = () => (
    <div className="flex justify-center w-full">
      {subscription?.stripe_subscription_id && (
        <Button onClick={handleViewInStripe} className="w-full">
          <ExternalLink className="mr-2 h-4 w-4" />
          View in Stripe
        </Button>
      )}
    </div>
  );

  // If no data provided, show not found
  if (!subscription) {
    return (
      <EntityDetailLayout title="Subscription Not Found" onClose={onClose}>
        <div className="py-6 text-center">
          <h3 className="text-lg font-medium mb-2">Subscription Not Found</h3>
          <p className="text-muted-foreground">
            The requested subscription could not be found or has been deleted.
          </p>
        </div>
      </EntityDetailLayout>
    );
  }

  // Helper function to get interval
  const getInterval = () => {
    // Use billing_interval if available, otherwise fallback to metadata or default
    return subscription.billing_interval || subscription.metadata?.interval || 'month';
  };
  
  // Helper function to format billing interval
  const formatBillingInterval = (interval: string) => {
    const intervalMap: Record<string, string> = {
      'day': 'Daily',
      'week': 'Weekly',
      'month': 'Monthly',
      'year': 'Yearly',
    };
    return intervalMap[interval.toLowerCase()] || interval;
  };

  return (
    <EntityDetailLayout 
      title={null} 
      onClose={onClose}
      footer={renderFooter()}
    >
      <div className="sticky top-0 z-10 mb-6 bg-[#F2F2F2] p-4 rounded-md">
        <div className="flex items-start justify-between mb-8">
          <h1 className="font-semibold text-3xl">
            Subscription Details
          </h1>
          <div className="flex gap-2 m-1.5">
            <Badge
              variant={subscription.status === "active" ? "default" : "secondary"}
              className={
                subscription.status === "active"
                  ? "bg-green-500 hover:bg-green-600"
                  : "bg-gray-400 hover:bg-gray-500"
              }
            >
              {subscription.status?.charAt(0).toUpperCase() +
                subscription.status?.slice(1) || "Unknown"}
            </Badge>
            {subscription.has_pending_upgrade && (
              <UpgradeStatusBadge 
                hasUpgrade={subscription.has_pending_upgrade}
                upgradeStatus={subscription.upgrade_status}
                gracePeriodEnd={subscription.grace_period_end}
                metadata={subscription.metadata}
              />
            )}
          </div>
        </div>
      </div>

      <div>
        {/* General Information */}
        <section className="mb-6 bg-[#F2F2F2] p-4 rounded-md">
          <h3 className="text-lg font-semibold mb-4">General Information</h3>
          <div className="grid grid-cols-2 gap-y-3 text-sm">
            {subscription.customer_name && (
              <div className="col-span-2">
                <h4 className="font-medium">Customer Name</h4>
                <p className="text-sm font-light text-foreground">
                  {subscription.customer_name}
                </p>
              </div>
            )}
            <div className="col-span-2">
              <h4 className="font-medium">Email</h4>
              <p className="text-sm font-light text-foreground">
                {subscription.customer_email || 'N/A'}
              </p>
            </div>
            <div className="col-span-2">
              <h4 className="font-medium">Price</h4>
              <p className="text-sm font-light text-foreground">
                {formatCurrency((subscription.amount || 0) / 100)} per {formatBillingInterval(getInterval())}
              </p>
            </div>
            <div className="col-span-2">
              <h4 className="font-medium">Billing Cycle</h4>
              <p className="text-sm font-light text-foreground">
                {formatBillingInterval(getInterval())}
              </p>
            </div>
            <div className="col-span-2">
              <h4 className="font-medium">Created</h4>
              <p className="text-sm font-light text-foreground">
                {subscription.created 
                  ? formatDate(new Date(subscription.created * 1000).toISOString()) 
                  : subscription.created_at 
                    ? formatDate(subscription.created_at)
                    : 'N/A'
                }
              </p>
            </div>
            <div className="col-span-2">
              <h4 className="font-medium">Current Period End</h4>
              <p className="text-sm font-light text-foreground">
                {subscription.current_period_end 
                  ? formatDate(subscription.current_period_end)
                  : 'N/A'}
              </p>
            </div>
            <div className="col-span-2">
              <h4 className="font-medium">ID</h4>
              <div className="flex items-center gap-2">
                <p className="font-mono text-xs text-foreground break-all flex-1">{id}</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleCopyId}
                  className="h-6 px-2 text-xs"
                >
                  <Copy className="h-3 w-3 mr-1" />
                  Copy
                </Button>
              </div>
            </div>
            {subscription.stripe_price_id && (
              <div className="col-span-2">
                <h4 className="font-medium">Price ID</h4>
                <p className="font-mono text-xs text-foreground break-all">{subscription.stripe_price_id}</p>
              </div>
            )}
          </div>
        </section>

        {subscription.cancel_at_period_end && (
          <section className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              ⚠️ This subscription will be cancelled at the end of the current billing period.
            </p>
          </section>
        )}
      </div>
    </EntityDetailLayout>
  );
};

export default SubscriptionDetailsView;